import api from './api';

// Obtener todas las ventas
const getSales = async () => {
  try {
    const response = await api.get('/api/sales');
    return response.data;
  } catch (error) {
    console.error('Error al obtener ventas:', error);
    throw error;
  }
};

// Obtener una venta por ID
const getSaleById = async (id, options = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Soporte para incluir relaciones
    if (options.include && Array.isArray(options.include)) {
      options.include.forEach(include => {
        params.append('include[]', include);
      });
    }
    
    const url = `${params.toString() ? `/api/sales/${id}?${params.toString()}` : `/api/sales/${id}`}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener venta:', error);
    throw error;
  }
};

// Crear una nueva venta
const createSale = async (saleData) => {
  try {
    const response = await api.post('/api/sales', saleData);
    return response.data;
  } catch (error) {
    console.error('Error al crear venta:', error);
    throw error;
  }
};

// Actualizar una venta
const updateSale = async (id, saleData) => {
  try {
    const response = await api.put(`/api/sales/${id}`, saleData);
    if (!response.data) {
      throw new Error('No se recibió respuesta del servidor');
    }
    return response.data;
  } catch (error) {
    console.error('Error al actualizar venta:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Eliminar una venta
const deleteSale = async (id) => {
  try {
    const response = await api.delete(`/api/sales/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar venta:', error);
    throw error;
  }
};

// Obtener estadísticas de ventas
const getSalesStats = async ({ timeRange, startDate, endDate, platformId, status, conversionFilter } = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (timeRange) params.append('timeRange', timeRange);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (platformId) params.append('platformId', platformId);
    if (status) params.append('status', status.join(','));
    if (conversionFilter) params.append('conversionFilter', conversionFilter);

    const response = await api.get(`/api/sales/stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de ventas:', error);
    throw error;
  }
};

// Obtener estadísticas de comisiones
const getCommissionStats = async ({ timeRange, startDate, endDate, platformId } = {}) => {
  try {
    const params = new URLSearchParams();

    console.log("Rango de fechas calculado getCommissionStats:", startDate, endDate);
    
    if (timeRange) params.append('timeRange', timeRange);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (platformId) params.append('platformId', platformId);

    const response = await api.get(`/api/sales/commission-stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de comisiones:', error);
    throw error;
  }
};

// Obtener ventas con filtros
const getSalesWithFilters = async (filters = {}) => {
  try {
    console.log('Enviando filtros al backend:', JSON.stringify(filters, null, 2));
    
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.status) params.append('status', filters.status);
    if (filters.article) params.append('article', filters.article);
    
    // Tratamiento especial para productCountry para diagnosticar problema
    if (filters.productCountry) {
      console.log('Tipo del valor productCountry:', typeof filters.productCountry);
      console.log('Valor de productCountry a enviar:', filters.productCountry);
      
      // Si productCountry es un objeto con isNull
      if (typeof filters.productCountry === 'object' && filters.productCountry.isNull) {
        console.log('productCountry tiene isNull=true, enviando parámetros anidados');
        params.append('productCountry[isNull]', 'true');
      } else {
        params.append('productCountry', filters.productCountry);
      }
    }
    
    if (filters.productCity) params.append('productCity', filters.productCity);
    if (filters.productId) params.append('productId', filters.productId);
    
    // Manejar el filtro de ID de cliente
    if (filters.customerId) {
      if (typeof filters.customerId === 'object') {
        if (filters.customerId.isNull) {
          params.append('customerId[isNull]', 'true');
        } else if (filters.customerId.isNotNull) {
          params.append('customerId[isNotNull]', 'true');
        }
      } else {
        params.append('customerId', filters.customerId);
      }
    }
    
    // Manejar el filtro de conversión
    if (filters.hasConversion !== undefined) {
      params.append('hasConversion', filters.hasConversion.toString());
    }
    
    // Añadir filtros de fecha de venta
    if (filters.dateRange?.startDate) params.append('dateRange[startDate]', filters.dateRange.startDate);
    if (filters.dateRange?.endDate) params.append('dateRange[endDate]', filters.dateRange.endDate);
    
    // Añadir filtros de fecha de realización
    if (filters.executionDateRange?.isNull) {
      params.append('executionDateRange[isNull]', 'true');
    } else {
      if (filters.executionDateRange?.startDate) params.append('executionDateRange[startDate]', filters.executionDateRange.startDate);
      if (filters.executionDateRange?.endDate) params.append('executionDateRange[endDate]', filters.executionDateRange.endDate);
    }
    
    // Añadir soporte para paginación
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    
    // Añadir soporte para ordenamiento
    if (filters.sortField) params.append('sortField', filters.sortField);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    
    // Soporte para incluir relaciones
    if (filters.include && Array.isArray(filters.include)) {
      filters.include.forEach(include => {
        params.append('include[]', include);
      });
    }

    // Incluir atributos de plataforma
    params.append('include[]', 'Platform');
    params.append('platformAttributes[]', 'name');
    params.append('platformAttributes[]', 'color');

    const response = await api.get(`/api/sales?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener ventas con filtros:', error);
    throw error;
  }
};

const uploadBulkSales = async (formData) => {
  try {
    // Mínima validación
    if (!formData.has('file') || !formData.has('platformId')) {
      throw new Error('Se requiere un archivo y una plataforma');
    }

    // Realizar la petición que ahora devuelve inmediatamente un jobId
    const response = await api.post('/api/sales/bulk', formData);
    return response.data;
  } catch (error) {
    console.error('Error en uploadBulkSales:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Error al subir ventas');
    }
  }
};

/**
 * Obtiene el estado de un trabajo de carga masiva
 * @param {string} jobId - Identificador del trabajo
 * @returns {Promise} - Promesa con el estado del trabajo
 */
const getBulkUploadStatus = async (jobId) => {
  try {
    const response = await api.get(`/api/sales/bulk-status/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estado del trabajo:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error('Error al verificar el estado del trabajo');
    }
  }
};

// Comprobar si una venta tiene conversión asociada
const checkSaleHasConversion = async (id) => {
  try {
    const response = await api.get(`/api/sales/${id}/has-conversion`);
    return response.data.hasConversion;
  } catch (error) {
    console.error('Error al verificar conversión:', error);
    throw error;
  }
};

/**
 * Obtiene solo los IDs de todas las ventas que cumplen con los filtros
 * @param {Object} filters Filtros para aplicar
 * @returns {Promise<Array>} Array con los IDs de las ventas
 */
const getAllSalesIds = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.status) params.append('status', filters.status);
    if (filters.article) params.append('article', filters.article);
    if (filters.productCountry) params.append('productCountry', filters.productCountry);
    if (filters.productCity) params.append('productCity', filters.productCity);
    if (filters.productId) params.append('productId', filters.productId);

    // Manejar el filtro de ID de cliente
    if (filters.customerId) {
      if (typeof filters.customerId === 'object') {
        if (filters.customerId.isNull) {
          params.append('customerId[isNull]', 'true');
        } else if (filters.customerId.isNotNull) {
          params.append('customerId[isNotNull]', 'true');
        }
      } else {
        params.append('customerId', filters.customerId);
      }
    }

    // Manejar el filtro de conversión
    if (filters.hasConversion !== undefined) {
      params.append('hasConversion', filters.hasConversion.toString());
    }

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.onlyIds) params.append('onlyIds', filters.onlyIds);

    // Añadir filtros de fecha de venta
    if (filters.dateRange?.startDate) params.append('dateRange[startDate]', filters.dateRange.startDate);
    if (filters.dateRange?.endDate) params.append('dateRange[endDate]', filters.dateRange.endDate);
    
    // Añadir filtros de fecha de realización
    if (filters.executionDateRange?.isNull) {
      params.append('executionDateRange[isNull]', 'true');
    } else {
      if (filters.executionDateRange?.startDate) params.append('executionDateRange[startDate]', filters.executionDateRange.startDate);
      if (filters.executionDateRange?.endDate) params.append('executionDateRange[endDate]', filters.executionDateRange.endDate);
    }

    const response = await api.get(`/api/sales/ids?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener IDs de ventas:', error);
    throw error;
  }
};

/**
 * Elimina múltiples ventas y sus conversiones asociadas
 * @param {Array} saleIds Array de IDs de ventas a eliminar
 * @returns {Promise<Object>} Resultado de la operación
 */
const bulkDeleteSales = async (saleIds) => {
  try {
    const response = await api.post('/api/sales/bulk-delete', { saleIds });
    return response.data;
  } catch (error) {
    console.error('Error al eliminar ventas en masa:', error);
    throw error;
  }
};

/**
 * Actualiza el estado de múltiples ventas
 * @param {Array} saleIds Array de IDs de ventas a actualizar
 * @param {string} newStatus Nuevo estado para las ventas
 * @returns {Promise<Object>} Resultado de la operación
 */
const bulkUpdateSalesStatus = async (saleIds, newStatus) => {
  try {
    const response = await api.post('/api/sales/bulk-update-status', { 
      saleIds, 
      status: newStatus 
    });
    return response.data;
  } catch (error) {
    console.error('Error al actualizar estado de ventas en masa:', error);
    throw error;
  }
};

// Obtener estadísticas de productos
const getProductStats = async (filters = {}) => {
  try {
    const { 
      timeRange, 
      startDate,
      endDate,
      country, 
      city, 
      platform, 
      hasConversion,
      page = 1,
      limit = 25,
      sortField = 'totalSales',
      sortOrder = 'DESC'
    } = filters;
    
    const params = new URLSearchParams();
    
    if (timeRange) params.append('timeRange', timeRange);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (country) params.append('country', country);
    if (city) params.append('city', city);
    if (platform) params.append('platform', platform);
    if (hasConversion !== undefined && hasConversion !== '') params.append('hasConversion', hasConversion);
    
    // Agregar parámetros de paginación y ordenación
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('sortField', sortField);
    params.append('sortOrder', sortOrder);

    console.log('🔧 [SERVICE] Parámetros enviados al backend:', params.toString());

    const response = await api.get(`/api/sales/product-stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error getting product stats:', error);
    throw error;
  }
};

// Obtener estadísticas por país
const getCountryStats = async (params) => {
  try {
    // Renombrar el parámetro 'platform' a 'platformId' para que coincida con lo que espera el backend
    const apiParams = {
      ...params
    };
    
    if (params.platform) {
      apiParams.platformId = params.platform;
      delete apiParams.platform;
    }
    
    console.log('Parámetros enviados a getCountryStats API:', apiParams);
    
    const response = await api.get('/api/sales/country-stats', { params: apiParams });
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas por país:', error);
    throw error;
  }
};

// Obtener estadísticas por ciudad
const getCityStats = async (params) => {
  try {
    // Renombrar el parámetro 'platform' a 'platformId' para que coincida con lo que espera el backend
    const apiParams = {
      ...params
    };
    
    if (params.platform) {
      apiParams.platformId = params.platform;
      delete apiParams.platform;
    }
    
    console.log('Parámetros enviados a getCityStats API:', apiParams);
    
    const response = await api.get('/api/sales/city-stats', { params: apiParams });
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas por ciudad:', error);
    throw error;
  }
};

// Obtener filtros de ubicación
const getLocationFilters = async () => {
  try {
    const response = await api.get('/api/sales/location-filters');
    return response.data;
  } catch (error) {
    console.error('Error al obtener filtros de ubicación:', error);
    throw error;
  }
};

const saleService = {
  getSales,
  getSaleById,
  createSale,
  updateSale,
  deleteSale,
  getSalesStats,
  getCommissionStats,
  getSalesWithFilters,
  uploadBulkSales,
  getBulkUploadStatus,
  checkSaleHasConversion,
  getAllSalesIds,
  bulkDeleteSales,
  bulkUpdateSalesStatus,
  getProductStats,
  getCountryStats,
  getCityStats,
  getLocationFilters
};

export default saleService; 
